import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { workersApi } from '@/services/workers'
import { formatDateOnly, parseServerDate } from '@/lib/tenant-date-time'
import type { Worker, WorkerAttendance } from '@/types'

export type PunchStatus = 'complete' | 'working' | 'missing' | 'absent'

export type AttendancePunch = WorkerAttendance & {
  worker?: string | number | null
}

export function todayIsoDate(timeZone?: string | null) {
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date())
    } catch {
      // Unknown zone name: fall back to the device calendar.
    }
  }
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const ORG_WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?[+-]\d{2}:?\d{2}$/

/**
 * `*_local` API values carry the org's UTC offset (e.g. `2026-10-13T01:15:00+05:30`).
 * Keep that wall clock instead of converting to the viewer's timezone, so a UK viewer
 * of an India org sees India time.
 */
function orgWallClock(value: string) {
  const match = ORG_WALL_CLOCK_PATTERN.exec(value.trim())
  if (!match) return null
  const [, year, month, day, hour, minute] = match.map(Number)
  const wallClock = new Date(Date.UTC(year, month - 1, day, hour, minute))
  return Number.isNaN(wallClock.getTime()) ? null : wallClock
}

export function addIsoDays(isoDate: string, days: number) {
  const [year, month, day] = isoDate.split('-').map(Number)
  const date = new Date(year, (month || 1) - 1, day || 1)
  date.setDate(date.getDate() + days)
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0')
  const nextDay = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${nextMonth}-${nextDay}`
}

export function workerName(worker: Pick<Worker, 'display_name' | 'full_name' | 'worker_name' | 'first_name' | 'last_name' | 'id'> | null | undefined) {
  if (!worker) return 'Worker'
  const combined = [worker.first_name, worker.last_name].filter(Boolean).join(' ').trim()
  return worker.display_name || worker.full_name || worker.worker_name || combined || `Worker ${worker.id}`
}

export function workerCode(worker: Worker | null | undefined) {
  return String(worker?.worker_code || worker?.user_registration_number || worker?.id || '').trim()
}

export function workerDepartment(worker: Worker | null | undefined) {
  return worker?.role_name || worker?.linked_post_role_name || worker?.worker_skill_mappings?.find((row) => row.is_primary)?.skill_role_title || '—'
}

export function workerTitle(worker: Worker | null | undefined) {
  const title = worker?.worker_skill_mappings?.find((row) => row.is_primary)?.skill_role_title || worker?.linked_post_role_name || ''
  const department = workerDepartment(worker)
  return title && title !== department ? title : ''
}

export function punchWorkerId(punch: AttendancePunch) {
  return String(punch.worker_id || punch.worker || '')
}

export function punchDate(punch: AttendancePunch) {
  const raw = punch.check_in_local || punch.check_in || punch.timestamp
  if (!raw) return ''
  if (orgWallClock(String(raw))) return String(raw).trim().slice(0, 10)
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return String(raw).slice(0, 10)
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}-${day}`
}

/**
 * Value to show for a punch time. With a tenant timezone the UTC instant is the source (the *_local copy
 * is in the worker's zone); without one the existing *_local-first value.
 */
export function punchDisplayValue(utcValue: string | null | undefined, localValue: string | null | undefined, timeZone?: string | null) {
  return timeZone ? utcValue || localValue : localValue || utcValue
}

/** Optional `timeZone` (tenant IANA zone) shows the instant in that zone; omitted keeps the browser zone. */
export function formatClock(value: string | null | undefined, timeZone?: string | null) {
  if (!value) return '—'
  // release: without a tenant timezone, *_local values keep the org's wall clock (orgWallClock)
  const wallClock = timeZone ? null : orgWallClock(value)
  if (wallClock) return wallClock.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
  const parsed = timeZone ? parseServerDate(value) : new Date(value)
  if (!parsed || Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
}

/** Optional `timeZone` shows instants on their tenant day and date-only values unshifted; omitted keeps the browser zone. */
export function formatDay(value: string | null | undefined, timeZone?: string | null) {
  if (!value) return '—'
  // release: without a tenant timezone, *_local values keep the org's wall clock (orgWallClock)
  const wallClock = timeZone ? null : orgWallClock(value)
  if (wallClock) return wallClock.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  if (timeZone) {
    const dateOnly = formatDateOnly(value, new Intl.DateTimeFormat().resolvedOptions().locale, options)
    if (dateOnly) return dateOnly
  }
  const parsed = timeZone ? parseServerDate(value) : new Date(value)
  if (!parsed || Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString([], { ...options, ...(timeZone ? { timeZone } : {}) })
}

export function workedLabel(punch: Pick<AttendancePunch, 'total_minutes' | 'check_in' | 'check_in_local' | 'check_out' | 'check_out_local'>) {
  let minutes: number | null = null
  if (punch.total_minutes != null && Number.isFinite(Number(punch.total_minutes))) {
    minutes = Number(punch.total_minutes)
  } else {
    // Whole clock minutes, matching the HH:MM shown for clock in/out.
    const start = Math.floor(new Date(punch.check_in_local || punch.check_in || '').getTime() / 60000)
    if (!Number.isFinite(start)) return '—'
    const endRaw = punch.check_out_local || punch.check_out
    const end = Math.floor((endRaw ? new Date(endRaw).getTime() : Date.now()) / 60000)
    if (!Number.isFinite(end)) return '—'
    minutes = Math.max(end - start, 0)
  }
  const wholeMinutes = Math.max(Math.round(minutes), 0)
  const hours = Math.floor(wholeMinutes / 60)
  const remainingMinutes = wholeMinutes % 60
  if (hours <= 0) return `${remainingMinutes}m`
  if (remainingMinutes === 0) return `${hours}h`
  return `${hours}h ${remainingMinutes}m`
}

export function punchStatus(punch: AttendancePunch, today = todayIsoDate()): PunchStatus {
  const day = punchDate(punch)
  if (!punch.check_in) return 'missing'
  if (!punch.check_out) return day && day < today ? 'missing' : 'working'
  return 'complete'
}

/** A review badge for punches let through outside their location's geofence. */
export function geofenceFlag(punch: Pick<AttendancePunch, 'geofence_check'>) {
  const checks = [
    ['Check-in', punch.geofence_check?.check_in],
    ['Check-out', punch.geofence_check?.check_out],
  ] as const
  const flagged = checks.filter(([, check]) => check && (check.status === 'outside' || check.status === 'missing_coordinates'))
  if (flagged.length === 0) return null
  const needsApproval = flagged.some(([, check]) => check?.action === 'approval')
  return {
    label: needsApproval ? 'Geofence: needs approval' : 'Outside geofence',
    title: flagged
      .map(([direction, check]) => {
        if (check?.status === 'missing_coordinates') return `${direction}: no GPS at ${check.location_name}`
        const away = check?.distance_m != null ? ` (${check.distance_m} m away)` : ''
        return `${direction}: outside ${check?.location_name}${away}`
      })
      .join('\n'),
  }
}

export function punchDeviceLabel(punch: Pick<AttendancePunch, 'attendance_device_name' | 'attendance_device_sn'> | null | undefined) {
  return String(punch?.attendance_device_name || punch?.attendance_device_sn || '').trim()
}

export function methodLabel(source: string | null | undefined) {
  if (source === 'adms') return 'Device'
  if (source === 'mobile') return 'Mobile'
  if (source === 'portal') return 'Portal'
  return source || '—'
}

export function useAttendanceDirectory() {
  const workersQuery = useQuery({
    queryKey: ['attendance-directory-workers'],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true }),
  })
  const workers = workersQuery.data?.results ?? []
  const byId = new Map(workers.map((worker) => [String(worker.worker_id ?? worker.id), worker]))
  workers.forEach((worker) => byId.set(String(worker.id), worker))
  return { workers, byId, isLoading: workersQuery.isLoading, error: workersQuery.error }
}

/** The org's IANA timezone, read from its most recent punch (falls back to the device zone). */
export function useAttendanceOrgTimeZone() {
  const query = useQuery({
    queryKey: ['attendance-org-timezone'],
    queryFn: () => workersApi.getWorkerAttendanceList({ page_size: 1, ordering: '-timestamp' }),
    staleTime: 10 * 60_000,
  })
  const latest = (query.data?.results ?? [])[0] as AttendancePunch | undefined
  return String(latest?.org_timezone ?? '').trim() || undefined
}

export function useAttendancePunches(startDate: string, endDate = startDate) {
  return useQuery({
    queryKey: ['attendance-punches', startDate, endDate],
    queryFn: async () => {
      const results: WorkerAttendance[] = []
      for (let page = 1; page <= 50; page += 1) {
        const response = await workersApi.getWorkerAttendanceList({
          start_date: startDate,
          end_date: endDate,
          page,
          page_size: 1000,
          ordering: '-timestamp',
        })
        results.push(...(response.results ?? []))
        if (!response.next || results.length >= Number(response.count || 0)) break
      }
      return { count: results.length, next: null, previous: null, results }
    },
  })
}

export type DateRange = { start: string; end: string }

function shiftIsoDate(iso: string, days: number) {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + days))
  return date.toISOString().slice(0, 10)
}

/** First and last day of a `YYYY-MM` month. */
export function monthRange(month: string): DateRange {
  const [year, monthIndex] = month.split('-').map(Number)
  const last = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate()
  return { start: `${month}-01`, end: `${month}-${String(last).padStart(2, '0')}` }
}

export type RangePreset = 'today' | 'yesterday' | 'this-week' | 'last-7' | 'this-month' | 'last-month' | 'month' | 'custom'

/** Resolves a preset against the org's today (weeks start on Monday). */
export function presetRange(preset: RangePreset, today: string): DateRange | null {
  switch (preset) {
    case 'today':
      return { start: today, end: today }
    case 'yesterday': {
      const day = shiftIsoDate(today, -1)
      return { start: day, end: day }
    }
    case 'this-week': {
      const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7
      return { start: shiftIsoDate(today, -weekday), end: today }
    }
    case 'last-7':
      return { start: shiftIsoDate(today, -6), end: today }
    case 'this-month':
      return { start: `${today.slice(0, 7)}-01`, end: today }
    case 'last-month':
      return monthRange(shiftIsoDate(`${today.slice(0, 7)}-01`, -1).slice(0, 7))
    default:
      return null
  }
}

export type AdminAttendancePayload = {
  workerId: string
  status: boolean
  timestamp?: string
  latitude?: number
  longitude?: number
  locationId?: string
}

export function useAttendancePunchActions() {
  const queryClient = useQueryClient()
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['attendance-directory-workers'] })
    await queryClient.invalidateQueries({ queryKey: ['attendance-punches'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance'] })
    await queryClient.invalidateQueries({ queryKey: ['worker-attendance-latest'] })
    await queryClient.invalidateQueries({ queryKey: ['attendance-board-day'] })
  }
  const record = useMutation({
    mutationFn: (payload: AdminAttendancePayload) => workersApi.createWorkerAttendance({
      worker: payload.workerId,
      status: payload.status,
      source: 'portal',
      ...(payload.timestamp ? { timestamp: payload.timestamp } : {}),
      ...(payload.latitude !== undefined ? { latitude: payload.latitude } : {}),
      ...(payload.longitude !== undefined ? { longitude: payload.longitude } : {}),
      ...(payload.locationId ? { job_location_id: payload.locationId } : {}),
    }),
    onSuccess: refresh,
  })
  return { record }
}
